import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { StudiesLayout } from "@/components/layouts";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useSegment } from "@/hooks/use-segment";
import { AssetUploadDropzone, useUploadsStore } from "@/components/ui/drag-drop-upload";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useGetAssets } from "@/api/studio/assets/queries";

/* Prop Types */
interface StudyVideoSelectRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudyVideoSelectRoute component
 *
 * Upload screen for video studies — accepts video files only via AssetUploadDropzone (mediaType="video").
 */
export const StudyVideoSelectRoute = ({ projectId, studyId }: StudyVideoSelectRouteProps) => {
  /* Hooks */
  const { segmentPageEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-select");

  /* Stores */
  const { reset } = useUploadsStore((state) => state.uploadActions);

  /* Queries */
  const { data: assetsData } = useGetAssets(projectId, studyId);

  /* Derived state */
  const canProceed = !!assetsData?.assets?.length;

  /* Side Effects */
  useEffect(() => {
    reset();
  }, [reset]);

  const handleUpload = () => {
    segmentPageEvent("Study Video Submitted", {
      projectId,
      studyId,
      studyType: "video",
    });
    navigateToNext();
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-select">
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Upload video</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" disabled={!canProceed} onClick={handleUpload}>
            Next: Process <ChevronRightIcon className="size-5" />
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <p className="text-base-content/70">Supported file types: MP4 and MOV.</p>
        </ToolbarItem>
      </Toolbar>

      {/* Drag & Drop */}
      <AssetUploadDropzone
        projectId={projectId}
        studyId={studyId}
        mediaType="video"
        dragDropText="or select a video from your computer"
        dragDropVideoUrl="/videos/upload-animation.webm"
        segmentTrackName="Study"
        segmentTrackMediaType="Video"
        additionalSegmentProperties={{ studyId, projectId, studyType: "video" }}
        selectAssetsText="Select video"
        firstColumnHeader="Video"
      />
    </StudiesLayout>
  );
};
